<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header" :class="{ 'page-header--scrolled': headerScrolled }">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button text="" :default-href="backHref"></ion-back-button>
        </ion-buttons>
        <ion-title>{{ isNew ? 'Новая подписка' : 'Редактирование' }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true" @ionScroll="onContentScroll">
      <form class="ion-padding event-edit-form" @submit.prevent="save">
        <ion-item>
          <ion-label position="stacked">Название *</ion-label>
          <ion-input v-model="form.title" required></ion-input>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Описание</ion-label>
          <ion-textarea v-model="form.description" :rows="4"></ion-textarea>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Срок действия (дней) *</ion-label>
          <ion-input type="number" v-model.number="form.durationDays" min="1" required></ion-input>
        </ion-item>
        <ion-item>
          <ion-toggle v-model="form.published">Опубликовано (видно пользователям)</ion-toggle>
        </ion-item>
        <div class="tiers-section">
          <h3>Тарифы</h3>
          <div v-for="(t, i) in form.priceTiers" :key="i" class="tier-row">
            <ion-input v-model="t.name" class="tier-input-name" placeholder="Название тарифа"></ion-input>
            <ion-input type="number" v-model.number="t.amountRub" class="tier-input-amount" placeholder="₽" min="0"></ion-input>
            <ion-select
              v-model="t.requiredAuthorityName"
              class="tier-role-select"
              interface="popover"
              placeholder="Роль (необяз.)"
              cancel-text="Отмена"
            >
              <ion-select-option :value="null">—</ion-select-option>
              <ion-select-option v-for="a in authorities" :key="a.name" :value="a.name">
                {{ a.description || a.name }}
              </ion-select-option>
            </ion-select>
            <ion-button fill="clear" size="small" class="tier-row-remove" @click="removeTier(i)">×</ion-button>
          </div>
          <ion-button fill="outline" size="small" class="tier-add-btn" @click="addTier">+ Тариф</ion-button>
        </div>
        <div class="tiers-section">
          <h3>Роли при покупке</h3>
          <ion-item>
            <ion-select
              label="Выдаваемые роли"
              label-placement="stacked"
              v-model="form.giftAuthorityNames"
              multiple
              interface="alert"
              cancel-text="Отмена"
              ok-text="Готово"
              placeholder="Выберите роли"
            >
              <ion-select-option v-for="a in authorities" :key="'gift-' + a.name" :value="a.name">
                {{ a.description || a.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>
        </div>
        <ion-item v-if="!isNew && subscriptionId">
          <ion-label>Логотип</ion-label>
          <input type="file" accept="image/*" @change="onLogoChange" ref="logoInput" style="display:none" />
          <ion-button fill="outline" @click="openLogoPicker">Загрузить</ion-button>
          <img v-if="logoPreview" :src="logoPreview" class="logo-preview" alt="" />
        </ion-item>
        <ion-button expand="block" type="submit" :disabled="saving" class="save-event-btn ion-margin-top">Сохранить</ion-button>
        <ion-button v-if="!isNew" expand="block" color="danger" fill="outline" :disabled="saving" @click="remove" class="ion-margin-top">
          Удалить подписку
        </ion-button>
      </form>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  alertController,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToggle,
  IonToolbar
} from '@ionic/vue';
import { defineComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { Authority } from '@/model/authority.model';
import AdminService from '@/services/admin.service';
import subscriptionsService from '@/services/subscriptions.service';

export default defineComponent({
  name: 'AdminSubscriptionEdit',
  components: {
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonTextarea,
    IonTitle,
    IonToggle,
    IonToolbar
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    return { route, router };
  },
  data() {
    return {
      adminService: new AdminService(),
      subscriptionId: null as number | null,
      saving: false,
      headerScrolled: false,
      authorities: [] as Authority[],
      logoFile: null as File | null,
      logoPreview: '',
      form: {
        title: '',
        description: '',
        durationDays: 30,
        published: true,
        giftAuthorityNames: [] as string[],
        priceTiers: [] as { name: string; amountRub: number; requiredAuthorityName: string | null }[]
      }
    };
  },
  computed: {
    isNew(): boolean {
      return this.route.name === 'AdminSubscriptionNew';
    },
    backHref(): string {
      return '/admin-subscriptions';
    }
  },
  async mounted() {
    try {
      // admin/authorities — полный каталог, без фильтра groupId > 0 из authorities-full
      const protectedRoles = ['ROLE_MANAGER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_SYSTEM'];
      const all = await this.adminService.getAdminAuthorities();
      this.authorities = (all || [])
        .filter((a) => a?.name && !protectedRoles.includes(a.name))
        .sort((a, b) => (a.description || a.name).localeCompare(b.description || b.name, 'ru'));
    } catch {
      this.authorities = [];
    }
    const id = Number(this.route.params.id);
    if (id && !this.isNew) {
      this.subscriptionId = id;
      const sub = await subscriptionsService.getSubscription(id);
      if (sub) {
        this.form.title = sub.title;
        this.form.description = sub.description || '';
        this.form.durationDays = sub.durationDays ?? 30;
        this.form.published = sub.published !== false;
        this.form.giftAuthorityNames = [...(sub.giftAuthorityNames || [])];
        this.form.priceTiers = (sub.priceTiers || []).map((t) => ({
          name: t.name,
          amountRub: t.amountKopecks / 100,
          requiredAuthorityName: t.requiredAuthorityName ?? null
        }));
        if (sub.logoPath) {
          this.logoPreview = subscriptionsService.logoUrl(sub.logoPath);
        }
      }
    } else if (this.isNew && this.form.priceTiers.length === 0) {
      this.form.priceTiers = [{ name: 'Стандарт', amountRub: 0, requiredAuthorityName: null }];
    }
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      this.headerScrolled = (e.detail?.scrollTop ?? 0) > 16;
    },
    addTier() {
      this.form.priceTiers.push({ name: '', amountRub: 0, requiredAuthorityName: null });
    },
    removeTier(i: number) {
      this.form.priceTiers.splice(i, 1);
    },
    openLogoPicker() {
      (this.$refs.logoInput as HTMLInputElement | undefined)?.click();
    },
    onLogoChange(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        this.logoFile = file;
        this.logoPreview = URL.createObjectURL(file);
      }
      (e.target as HTMLInputElement).value = '';
    },
    async save() {
      this.saving = true;
      try {
        const tiers = this.form.priceTiers
          .filter((t) => t.name.trim())
          .map((t) => ({
            name: t.name.trim(),
            amountKopecks: Math.round(t.amountRub * 100),
            requiredAuthorityName: t.requiredAuthorityName || null
          }));
        if (this.isNew) {
          const created = await subscriptionsService.createSubscription({
            title: this.form.title.trim(),
            description: this.form.description || undefined,
            durationDays: this.form.durationDays,
            published: this.form.published
          });
          if (tiers.length) {
            await subscriptionsService.setPriceTiers(created.id, tiers);
          }
          if (this.form.giftAuthorityNames.length) {
            await subscriptionsService.setGiftAuthorities(created.id, this.form.giftAuthorityNames);
          }
          this.subscriptionId = created.id;
          if (this.logoFile) {
            await subscriptionsService.uploadLogo(created.id, this.logoFile);
          }
          this.router.replace('/admin-subscriptions');
        } else if (this.subscriptionId) {
          await subscriptionsService.updateSubscription(this.subscriptionId, {
            title: this.form.title.trim(),
            description: this.form.description ?? undefined,
            durationDays: this.form.durationDays,
            published: this.form.published
          });
          await subscriptionsService.setPriceTiers(this.subscriptionId, tiers);
          await subscriptionsService.setGiftAuthorities(this.subscriptionId, this.form.giftAuthorityNames);
          if (this.logoFile) {
            await subscriptionsService.uploadLogo(this.subscriptionId, this.logoFile);
          }
          this.router.replace('/admin-subscriptions');
        }
      } catch (e) {
        console.error(e);
      }
      this.saving = false;
    },
    async remove() {
      const id = this.subscriptionId;
      if (id == null) return;
      const alert = await alertController.create({
        header: 'Удаление подписки',
        message: 'Удалить эту подписку? Действие нельзя отменить.',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await subscriptionsService.deleteSubscription(id);
                this.router.replace('/admin-subscriptions');
              } catch (e) {
                console.error(e);
              }
            }
          }
        ]
      });
      await alert.present();
    }
  }
});
</script>

<style scoped>
.tiers-section {
  margin: 16px 0;
  color: var(--liga-chrome-text);
}
.tiers-section h3 {
  color: var(--liga-chrome-text);
  margin-bottom: 12px;
}
.tier-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.tier-row ion-input {
  flex: 1;
  min-width: 100px;
  --color: var(--liga-chrome-text);
}
.tier-input-amount {
  max-width: 100px;
}
.tier-role-select {
  flex: 1;
  min-width: 140px;
  --color: var(--liga-chrome-text);
}
.tier-row-remove {
  flex-shrink: 0;
  --color: #e85d04;
}
.tier-add-btn,
.save-event-btn {
  --background: #e85d04;
  --color: var(--liga-chrome-text);
  font-weight: 600;
}
form ion-item {
  --background: var(--liga-surface);
  --color: var(--liga-chrome-text);
}
.logo-preview {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  margin-left: 8px;
}
</style>
